<template>
  <div class="chat-container">
    <div class="chat-header d-flex justify-between">
      <h4>Pre-Checkin Chat</h4>
      <b-button variant="success" class="m-2" v-if="role === 'admin'" @click="showExtraModal = true">
        Add Extra Amount
      </b-button>
      <b-button variant="warning" class="m-2" v-if="role === 'admin'" @click="showEditTable = !showEditTable">
        {{ showEditTable ? 'Hide' : 'Edit Extra Charges' }}
      </b-button>
      <b-button variant="primary" class="m-2" @click="generateCheckoutBill(bookingId)">
        Check Invoice
      </b-button>
    </div>
    <div class="chat-messages" ref="chatMessages">

      <div v-for="msg in messages" :key="msg.id"
        :class="['chat-bubble', ((msg.sender === role) || ((role != 'admin') && (msg.sender === 'customer'))) ? 'current-user' : 'other-user']">
        <div class="chat-meta">
          <span class="chat-user">{{ msg.is_admin ? 'Admin' : msg.user_name }}</span>
          <span class="chat-time">{{ formatDateTime(msg.created_at) }}</span>
        </div>
        <div class="chat-text" v-html="msg.message"></div>
      </div>
      <div v-if="loading" class="text-center my-3">
        <b-spinner small /> Loading...
      </div>
      <div v-if="error" class="alert alert-danger">{{ error }}</div>
    </div>
    <div class="chat-input">
      <b-form @submit.prevent="sendMessage">
        <b-input-group>
          <b-form-input v-model="newMessage" placeholder="Type your message..." :disabled="sending"
            @keyup.enter="sendMessage" />
          <b-input-group-append>
            <b-button type="submit" variant="primary" :disabled="sending || !newMessage">
              <i class="fas fa-paper-plane"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form>
    </div>
    <b-modal ref="extraModal" v-model="showExtraModal" title="Add Extra Amount" :hide-footer="true">
      <b-form @submit.prevent="submitExtraAmount">
        <b-form-group :label="$t('billing.category')" label-for="category-select" required>
          <b-form-select id="category-select" v-model="extraAmount.category" :options="categoryOptions" required>
            <template #first>
              <b-form-select-option value="" disabled>
                {{ $t("billing.select_category") }}
              </b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Item Name">
          <b-form-input v-model="extraAmount.item_name" required />
        </b-form-group>

        <b-form-group label="Amount" label-for="amount-input">
          <b-form-input id="amount-input" v-model="extraAmount.amount" type="number" min="0" required
            placeholder="Enter amount" />
        </b-form-group>

        <b-form-group label="Description" label-for="desc-input">
          <b-form-textarea id="desc-input" v-model="extraAmount.description" placeholder="Enter description" rows="3" />
        </b-form-group>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <div class="modal-footer d-flex mr-2 gap-2 mt-4">
          <b-button variant="secondary" @click="showExtraModal = false">{{ $t('common.cancel') }}</b-button>
          <b-button type="submit" variant="primary">{{ $t('common.save') }}</b-button>
        </div>
      </b-form>
    </b-modal>
    <div v-if="showEditTable" class="my-3">
      <h5>Extra Charges</h5>
      <b-table :items="extraCharges" :fields="['item_name', 'amount', 'category', 'actions']" small>
        <template #cell(actions)="data">
          <b-button size="sm" @click="editExtraAmount(data.item)" variant="warning">Edit</b-button>
        </template>
      </b-table>
    </div>
    <b-modal v-model="showEditModal" title="Edit Extra Amount" :hide-footer="true">
      <b-form @submit.prevent="submitEditAmount">
        <b-form-group label="Category">
          <b-form-select v-model="editModalData.category" :options="categoryOptions" required />
        </b-form-group>

        <b-form-group label="Item Name">
          <b-form-input v-model="editModalData.item_name" required />
        </b-form-group>

        <b-form-group label="Amount">
          <b-form-input type="number" v-model="editModalData.amount" required />
        </b-form-group>

        <b-form-group label="Description">
          <b-form-textarea v-model="editModalData.description" rows="3" />
        </b-form-group>

        <div class="modal-footer d-flex justify-end">
          <b-button variant="secondary" @click="showEditModal = false">Cancel</b-button>
          <b-button type="submit" variant="primary">Save</b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import { mapGetters } from "vuex";

export default {
  name: "PreCheckinChat",
  data() {
    return {
      messages: [],
      loading: false,
      error: null,
      newMessage: "",
      sending: false,
      role: null,
      showExtraModal: false,
      extraAmount: {
        amount: null,
        description: "",
        category: "",
        item_name: "",
      },
      categories: {},
      showEditTable: false,
      extraCharges: [],
      editModalData: {
        id: null,
        amount: null,
        item_name: '',
        description: '',
        category: '',
      },
      showEditModal: false,

    };
  },
  computed: {
    ...mapGetters(['user']),
    categoryOptions() {
      const options = [{ value: "", text: "All Categories" }];
      Object.keys(this.categories).forEach((key) => {
        options.push({ value: key, text: this.categories[key] });
      });
      return options;
    },
    bookingId() {
      return this.$route.query.booking_id;
    },
    preCheckinId() {
      return this.$route.query.pre_checkin_id;
    },
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await request({
          method: "get",
          url: "/billing/categories",
        });
        this.categories = response.data;
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },
    async fetchMessages() {
      this.loading = true;
      this.error = null;
      try {
        const res = await request({
          method: "get",
          url: "/conversations",
          params: {
            booking_id: this.bookingId,
            pre_checkin_id: this.preCheckinId,
          },
        });
        this.messages = res.data || res || [];
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (e) {
        this.error = "Failed to load chat messages.";
      } finally {
        this.loading = false;
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;
      this.sending = true;
      try {

        const sender = this.role === 'admin' ? 'admin' : 'customer';
        await request({
          method: "post",
          url: "/conversations",
          data: {
            booking_id: this.bookingId,
            pre_checkin_id: this.preCheckinId,
            category: this.category,
            sender: sender,
            message: this.newMessage,
          },
        });
        this.newMessage = "";
        await this.fetchMessages();
      } catch (e) {
        this.error = "Failed to send message.";
      } finally {
        this.sending = false;
      }
    },
    async fetchExtraCharges() {
      try {
        const res = await request({
          method: "get",
          url: `/booking/${this.bookingId}/extra-amounts`,
        });
        this.extraCharges = res;

        console.log('extraCharges', this.extraCharges);
      } catch (error) {
        console.error("Failed to fetch extra amounts", error);
      }
    },
    editExtraAmount(item) {
      this.editModalData = {
        id: item.id,
        amount: item.amount,
        item_name: item.item_name,
        description: item.description,
        category: item.category,
      };
      this.showEditModal = true;
      this.showEditTable = true;
    },

    async submitEditAmount() {
      try {
        await request({
          method: "put",
          url: `/billing/${this.editModalData.id}/update`,
          data: this.editModalData,
        });

        this.$bvToast.toast("Amount updated successfully", {
          variant: "success",
          solid: true,
        });

        this.fetchExtraCharges();
        this.fetchMessages();
        this.showEditModal = false;
        this.showEditTable = false;
      } catch (e) {
        this.$bvToast.toast("Failed to update amount", {
          variant: "danger",
          solid: true,
        });
      }
    },
    formatDateTime(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleString();
    },
    scrollToBottom() {
      const el = this.$refs.chatMessages;
      if (el) el.scrollTop = el.scrollHeight;
    },

    async generateCheckoutBill(bookingId) {
      try {
        const response = await request({
          method: "get",
          url: `/bookings/${bookingId}/checkout-bill`,
        });

        this.$bvToast.toast(this.$t("billing.checkout_generated_successfully"), {
          title: this.$t("common.success"),
          variant: "success",
          solid: true,
        });
        // console.log(response.success);
        if (response && response.pdf_url) {
          window.open(response.pdf_url, "_blank");
        }
      } catch (error) {
        console.error("Error generating checkout bill:", error);
        this.$bvToast.toast(this.$t("billing.failed_to_generate_checkout"), {
          title: this.$t("common.error"),
          variant: "danger",
          solid: true,
        });
      }
    },
    async submitExtraAmount() {
      if (!this.extraAmount.amount || !this.extraAmount.description) {
        this.error = "Please fill in all fields.";
        return;
      }

      try {
        await request({
          method: "post",
          url: "/add-extra-amount",
          data: {
            booking_id: this.bookingId,
            pre_checkin_id: this.preCheckinId,
            amount: this.extraAmount.amount,
            category: this.extraAmount.category,
            description: this.extraAmount.description,
            item_name: this.extraAmount.item_name,
          },
        });

        this.$bvToast.toast("Extra amount added successfully", {
          variant: "success",
          solid: true,
        });

        // Clear and close modal
        this.extraAmount.amount = null;
        this.extraAmount.item_name = null;
        this.extraAmount.description = "";
        this.error = null;
        this.$refs.extraModal.hide();
        await this.fetchMessages();
      } catch (e) {
        this.error = "Failed to submit extra amount.";
      }
    }
  },
  mounted() {
    this.fetchMessages();
    if (this.user) {
      this.role = this.user.role;
    }
  },
  created() {
    this.fetchCategories();
    this.fetchExtraCharges();
  }
};
</script>

<style scoped>
.chat-container {
  max-width: 760px;
  margin: 30px auto;
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 70vh;
}

.chat-header {
  padding: 1rem;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 10px 10px 0 0;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: #f5f7fa;
}

.chat-bubble {
  max-width: 56%;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 18px;
  background: #e9ecef;
  position: relative;
  word-break: break-word;
}

.chat-bubble.current-user {
  background: #d1e7dd;
  margin-left: auto;
  text-align: right;
}

.chat-bubble.other-user {
  background: #fff;
  margin-right: auto;
  text-align: left;
  border: 1px solid #e3e3e3;
}

.chat-meta {
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 0.2rem;
}

.chat-input {
  padding: 1rem;
  border-top: 1px solid #eee;
  background: #f8f9fa;
  border-radius: 0 0 10px 10px;
}
</style>
